/**
 * Tipi e funzioni "pure" del gioco Globe (no DOM, no fetch).
 * Testabili in isolamento.
 */

import distance from '@turf/distance';
import bearing from '@turf/bearing';
import { point } from '@turf/helpers';

export interface Country {
  /** ISO 3166-1 alpha-3. */
  iso3: string;
  /** ISO 3166-1 alpha-2 (usato per le bandiere). */
  cca2: string;
  /** Nome canonico (italiano o inglese). */
  name: string;
  /** Emoji bandiera (fallback). */
  flag: string;
  /** Aliasses ricerca (lowercased) IT + EN + comuni. */
  aliases: string[];
  /** [lon, lat] centroide. */
  centroid: [number, number];
  continent: string;
  /** ISO3 dei paesi confinanti (da REST Countries). */
  borders?: string[];
}

export interface CountriesData {
  countries: Country[];
}

export interface GlobeState {
  /** ISO3 del paese misterioso. */
  targetIso3: string;
  /** Tentativi (ISO3 in ordine). */
  guesses: string[];
  /** Cache distanza in km per iso3 tentato. */
  distancesKm: Record<string, number>;
  /** Cache bearing (gradi 0=N, 90=E) dal tentativo verso il target. */
  bearings: Record<string, number>;
  /** Daily mode true/false (per riferimento, non cambia logica). */
  daily: boolean;
  /** True se il giocatore si è arreso e il target è stato rivelato. */
  surrendered?: boolean;
  hintsRevealed?: number;
}

export interface GlobeGuess {
  iso3: string;
  cca2: string;
  name: string;
  flag: string;
}

export const MAX_DISTANCE_KM = 20015; // mezza circonferenza terrestre

/** Distanza in km tra due country (centroidi). */
export function countryDistance(a: Country, b: Country): number {
  return distance(point(a.centroid), point(b.centroid), { units: 'kilometers' });
}

export function countryBearing(from: Country, to: Country): number {
  // bearing 0..360 (rispetto a Nord)
  const b = bearing(point(from.centroid), point(to.centroid));
  return (b + 360) % 360;
}

/** Restituisce direzione cardinale come chiave i18n suffisso ('N','NE',...). */
export function cardinal(deg: number): 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
  const idx = Math.round(deg / 45) % 8;
  return dirs[idx];
}

/** Scala colore Globle-faithful: rosso vivido (vicino) -> rosa pallido (lontano).
 *  Stessa famiglia cromatica di Globle (intensità di rosso) per leggibilità immediata. */
export function distanceColor(km: number): string {
  // Curva non lineare per dare più risoluzione ai piccoli km
  const t = Math.min(1, Math.sqrt(km / MAX_DISTANCE_KM));
  const hue = Math.round(t * 20); // 0 (rosso puro) -> 20 (rosa caldo)
  const sat = Math.round(90 - 60 * t); // 90% -> 30%
  const light = Math.round(38 + 47 * t); // 38% (vivido scuro) -> 85% (pallido)
  // Sintassi comma-separated: d3-color (usato da globe.gl) richiede questa forma.
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

/** Haversine diretta (evita la creazione di oggetti GeoJSON nel loop). */
function haversineKm(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Estrae tutti i vertici [lon, lat] da un'unica feature GeoJSON (Polygon o MultiPolygon). */
function extractVerts(geom: unknown): [number, number][] {
  const g = geom as { type: string; coordinates: unknown };
  const verts: [number, number][] = [];
  if (g?.type === 'Polygon') {
    for (const ring of g.coordinates as number[][][]) {
      for (const c of ring) verts.push([c[0], c[1]]);
    }
  } else if (g?.type === 'MultiPolygon') {
    for (const poly of g.coordinates as number[][][][]) {
      for (const ring of poly) {
        for (const c of ring) verts.push([c[0], c[1]]);
      }
    }
  }
  return verts;
}

/** Campiona al massimo `max` vertici uniformemente per limitare O(n*m). */
function sampleVerts(verts: [number, number][], max: number): [number, number][] {
  if (verts.length <= max) return verts;
  const step = Math.ceil(verts.length / max);
  return verts.filter((_, i) => i % step === 0);
}

type GeoFeat = { properties: Record<string, unknown>; geometry: unknown };

const sampledVertexCache = new WeakMap<readonly GeoFeat[], Map<string, [number, number][]>>();

function sampledVerticesByIso(geoFeatures: readonly GeoFeat[]): Map<string, [number, number][]> {
  const cached = sampledVertexCache.get(geoFeatures);
  if (cached) return cached;

  const byIso = new Map<string, [number, number][]>();
  for (const feature of geoFeatures) {
    const iso = (feature.properties['ISO_A3'] ?? feature.properties['ADM0_A3']) as string | undefined;
    if (!iso) continue;
    byIso.set(iso, sampleVerts(extractVerts(feature.geometry), 500));
  }
  sampledVertexCache.set(geoFeatures, byIso);
  return byIso;
}

/**
 * Distanza confine-confine tra due paesi.
 * - 0 km se condividono un confine (da `borders`).
 * - Minimo tra i vertici campionati dei rispettivi poligoni.
 * - Fallback al centroide se uno dei due è un micro-stato senza geometria.
 */
export function borderDistance(
  guessed: Country,
  target: Country,
  geoFeatures: readonly GeoFeat[],
): number {
  // Caso 1: paese tentato == target
  if (guessed.iso3 === target.iso3) return 0;

  // Caso 2: confinanti → 0 km
  if (
    (guessed.borders ?? []).includes(target.iso3) ||
    (target.borders ?? []).includes(guessed.iso3)
  ) {
    return 0;
  }

  const vertsByIso = sampledVerticesByIso(geoFeatures);
  const vertsA = vertsByIso.get(guessed.iso3) ?? [];
  const vertsB = vertsByIso.get(target.iso3) ?? [];

  // Caso 3: micro-stato senza geometria → fallback centroide
  if (vertsA.length === 0 || vertsB.length === 0) {
    return countryDistance(guessed, target);
  }

  let minKm = Infinity;
  for (const [ax, ay] of vertsA) {
    for (const [bx, by] of vertsB) {
      const d = haversineKm(ax, ay, bx, by);
      if (d < minKm) minKm = d;
    }
  }
  return minKm;
}

/** Trova country per input testuale (case-insensitive, alias). */
export function findCountry(input: string, list: Country[]): Country | null {
  const q = normalizeCountryInput(input);
  if (!q) return null;
  // Match esatto su nome o alias
  for (const c of list) {
    if ([c.name, c.iso3, c.cca2, ...c.aliases].some((a) => normalizeCountryInput(a) === q)) return c;
  }
  // Match prefix
  const matches = list.filter((c) => [c.name, ...c.aliases].some((a) => normalizeCountryInput(a).startsWith(q)));
  return matches.length === 1 ? matches[0] : null;
}

export function normalizeCountryInput(input: string): string {
  return input.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[’']/g, ' ').replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function autocomplete(input: string, list: Country[], limit = 8): Country[] {
  const q = normalizeCountryInput(input);
  if (!q) return [];
  const out: Country[] = [];
  for (const c of list) {
    if (
      normalizeCountryInput(c.name).includes(q) ||
      c.aliases.some((a) => normalizeCountryInput(a).includes(q))
    ) {
      out.push(c);
      if (out.length >= limit) break;
    }
  }
  return out;
}
